import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Video, Clock, CheckSquare, Youtube, Music, Users, ArrowRight, Sparkles, Heart, Play, HelpCircle, Briefcase, Coffee, Timer, LayoutGrid } from "lucide-react";

// FAQ data for schema markup
const faqData = [
  {
    question: "What happened to Groove?",
    answer: "Groove, the virtual coworking app, shut down in mid-2025 after struggling to sustain its subscription model. CSW (Chill Study Work) is now the best free Groove alternative with Study Stream, Study Timer, and Task Management features."
  },
  {
    question: "Is CSW a free Groove alternative?",
    answer: "Yes, CSW is completely free to use with no limits. You can join unlimited study and coworking streams, use the Pomodoro timer, and manage tasks without any subscription fees—unlike Groove's $18/month model."
  },
  {
    question: "What features does CSW offer compared to Groove?",
    answer: "CSW offers 24/7 study rooms for real-time coworking sessions, customizable Study Timer with Pomodoro technique, Task Management with Kanban boards, synchronized YouTube watching, and lo-fi music playlists—all free."
  },
  {
    question: "Can I cowork with others on CSW like Groove?",
    answer: "Absolutely! CSW's Study Stream feature lets you join or host live coworking sessions with people worldwide, creating the same accountability and companionship that Groove offered."
  },
  {
    question: "Does CSW have structured sessions like Groove?",
    answer: "Yes, CSW has a fully customizable timer with Pomodoro-style intervals, allowing you to structure work sessions with breaks just like Groove's 50-minute format."
  },
  {
    question: "Is CSW better than Groove was?",
    answer: "CSW offers more features than Groove—including unlimited 24/7 streams, task management, quizzes, flash cards, and themed study rooms—all completely free with no subscription required."
  }
];

const Groove = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Groove Alternative - Free Virtual Coworking"
        description="Looking for a Groove alternative? Groove shut down in 2025 but CSW offers free virtual coworking with Study Stream, Study Timer, and Task Management. Start working smarter today."
        keywords="groove alternative, groove app, groove coworking, virtual coworking, body doubling, focusmate alternative, remote work productivity"
        canonicalUrl="/groove/"
      />
      
      {/* WebPage Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Groove Alternative - Free Virtual Coworking | CSW",
        "description": "Looking for a Groove alternative? Groove shut down in 2025 but CSW offers free virtual coworking with Study Stream, Study Timer, and Task Management.",
        "url": "https://csw.live/groove/",
        "inLanguage": "en",
        "isPartOf": {
          "@type": "WebSite",
          "name": "CSW - Chill Study Work",
          "url": "https://csw.live"
        },
        "about": {
          "@type": "Thing",
          "name": "Groove Alternative"
        },
        "mentions": [
          { "@type": "Thing", "name": "Groove App" },
          { "@type": "Thing", "name": "Virtual Coworking" },
          { "@type": "Thing", "name": "Body Doubling" },
          { "@type": "Thing", "name": "Pomodoro Timer" }
        ],
        "keywords": "groove alternative, groove app, groove coworking, virtual coworking, body doubling, focusmate alternative, remote work productivity"
      })}} />
      
      {/* SoftwareApplication Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "CSW - Groove Alternative",
        "applicationCategory": "EducationalApplication",
        "operatingSystem": "Web Browser",
        "description": "Free Groove alternative with virtual coworking rooms, Study Timer, and Task Management. Join unlimited sessions with no time limits or subscription fees.",
        "url": "https://csw.live/groove/",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "5",
          "ratingCount": "1000",
          "bestRating": "5",
          "worstRating": "1"
        },
        "featureList": [
          "Free Virtual Coworking",
          "24/7 Study Rooms",
          "Customizable Pomodoro Timer",
          "Task Management with Kanban",
          "Body Doubling Sessions",
          "No Subscription Required",
          "Global Community",
          "Lo-fi Music Integration"
        ],
        "publisher": {
          "@type": "Organization",
          "name": "CSW - Chill Study Work",
          "url": "https://csw.live"
        }
      })}} />
      
      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      })}} />
      
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in">
              <span className="text-gradient">Groove</span>
            </h1>
            
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Groove Alternative
            </h1>
            
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight animate-fade-in text-muted-foreground" style={{ animationDelay: '0.2s' }}>
              Free Virtual Coworking{" "}
              <span className="text-gradient">24/7</span>
            </h2>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              When Groove shut down in 2025, thousands of freelancers and remote workers lost their virtual coworking home. CSW fills that void with free, unlimited coworking sessions—no subscription required. Join our global community of <Link to="/study-with-me/" className="text-primary hover:underline">productive learners and workers</Link>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start Coworking Free <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Heart className="w-4 h-4 text-primary fill-primary" />
                  <span className="font-medium">Free Forever</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="font-medium">No Limits</span>
                </div>
              </div>
            </div>
            
            {/* Video Section */}
            <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                    <Play className="w-5 h-5 text-primary-foreground ml-1" />
                  </div>
                  <span className="text-foreground font-medium">See CSW in action</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Groove Shutdown Notice */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="max-w-4xl mx-auto p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-primary/20">
            <div className="space-y-6">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center flex-shrink-0">
                  <Coffee className="w-7 h-7 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
                    Groove Has <span className="text-gradient">Shut Down</span>
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    Groove—the beloved virtual coworking app that helped freelancers and remote workers beat isolation—quietly shut down in mid-2025. Despite its devoted community of "Groovers" who logged 1.5+ million minutes of focused work, the platform couldn't sustain its subscription model. <Link to="/groove/history/" className="text-primary hover:underline font-medium">Read the full Groove story →</Link>
                  </p>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    <span className="font-semibold text-foreground">CSW – Chill Study Work</span> is here to fill the void with a free, feature-rich alternative. Explore our <Link to="/online-workspace/" className="text-primary hover:underline">Online Workspace</Link>, <Link to="/productivity-tool/" className="text-primary hover:underline">Productivity Tools</Link>, and <Link to="/study-with-friends/" className="text-primary hover:underline">collaborative modes</Link>.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Multiple Modes Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20 text-xs font-medium text-secondary">
                CHILL • STUDY • WORK
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Multiple <span className="text-gradient">Modes</span>
              </h2>
              <p className="text-xl text-muted-foreground font-light">
                Unlike Groove's single format, CSW adapts to your needs.
              </p>
              <div className="space-y-4">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  CSW has three main modes—<strong>Chill</strong>, <strong>Study</strong>, and <strong>Work</strong>—each tailored for different activities. Chill mode lets you stream videos or host watch parties; Study mode includes Pomodoro timers and group rooms; Work mode offers task lists and reminders for projects.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Whether you're a student, freelancer, or remote employee, you'll find a mode that fits your workflow. Discover how <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="text-primary hover:underline">body doubling can transform your productivity</Link>.
                </p>
              </div>
            </div>
            <Card className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
              <div className="aspect-video bg-muted/50 rounded-xl flex items-center justify-center">
                <LayoutGrid className="w-16 h-16 text-muted-foreground/50" />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">Flexible Modes for Every Need</p>
            </Card>
          </div>
        </div>
      </section>

      {/* 24/7 Study Rooms Feature */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <Card className="p-8 order-2 lg:order-1 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="aspect-video bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center">
                <Users className="w-16 h-16 text-muted-foreground/50" />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">Real-Time Virtual Coworking</p>
            </Card>
            <div className="space-y-6 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-medium text-primary">
                ALWAYS OPEN
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                24/7 <span className="text-gradient">Study Rooms</span>
              </h2>
              <div className="space-y-4">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Unlike Groove's scheduled sessions, CSW's <Link to="/study-stream/" className="text-primary hover:underline">study rooms</Link> are always open. Jump in anytime—whether you're an early bird or a night owl—and work alongside peers from around the world.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  CSW operates 24/7 with no caps on session duration, providing the same accountability and companionship that Groovers loved. Join our <Link to="/group-study/" className="text-primary hover:underline">group study sessions</Link> to experience the power of <Link to="/study-with-strangers/" className="text-primary hover:underline">working with strangers</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pomodoro Timer Feature */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-secondary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Pomodoro <span className="text-gradient">Timer</span>
              </h2>
              <div className="space-y-4">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Groove's 50-minute structure echoed the Pomodoro technique. CSW takes it further with a fully customizable <Link to="/study-timer/" className="text-primary hover:underline">study timer</Link>—adjust intervals, set visual and audio cues, and personalize backgrounds.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  The timer integrates into study rooms, helping you maintain focus together. With structured work periods and breaks, you'll stay productive without burning out. Learn about <Link to="/blog/top-10-digital-tools-for-academic-success/" className="text-primary hover:underline">more digital tools for success</Link>.
                </p>
              </div>
            </div>
            <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="aspect-video bg-gradient-to-br from-secondary/10 to-primary/10 rounded-xl flex items-center justify-center">
                <Timer className="w-16 h-16 text-muted-foreground/50" />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">Customizable Pomodoro Timer</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Task Management Feature */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <Card className="p-8 order-2 lg:order-1 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="aspect-video bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center">
                <CheckSquare className="w-16 h-16 text-muted-foreground/50" />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">Kanban Task Management</p>
            </Card>
            <div className="space-y-6 order-1 lg:order-2">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Task <span className="text-gradient">Management</span>
              </h2>
              <div className="space-y-4">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Groove asked users to articulate goals at the start of each session. CSW goes further with built-in <Link to="/task-management/" className="text-primary hover:underline">task management</Link>—create tasks, set deadlines, organize in Kanban boards, and track progress.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Never miss a deadline with our <Link to="/reminders/" className="text-primary hover:underline">smart reminders</Link>. Turn your session goals into actionable items that persist beyond each work block. Check out tips to <Link to="/blog/how-to-stop-procrastination/" className="text-primary hover:underline">stop procrastination</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community & Atmosphere Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Community & <span className="text-gradient">Atmosphere</span>
              </h2>
              <div className="space-y-4">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Groove's magic was its intimate community. CSW captures that spirit with a global network of learners and workers—89% of users report feeling more connected, and productivity increases 2.5x when coworking together.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Personalize your workspace with <Link to="/study-stream/" className="text-primary hover:underline">lo-fi music</Link>, themed backgrounds, and even <Link to="/study-with-kpop/" className="text-primary hover:underline">K-Pop themed study rooms</Link>. The atmosphere you loved in Groove lives on. Explore the <Link to="/blog/science-behind-study-with-me/" className="text-primary hover:underline">science behind studying together</Link>.
                </p>
              </div>
            </div>
            <Card className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
              <div className="aspect-video bg-muted/50 rounded-xl flex items-center justify-center">
                <Music className="w-16 h-16 text-muted-foreground/50" />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">Customizable Study Atmosphere</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground mb-6">
              CSW vs <span className="text-gradient">Groove</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              Everything Groove offered—and more—completely free.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="grid grid-cols-3 gap-4 text-center mb-6">
                <div className="font-semibold text-foreground">Feature</div>
                <div className="font-semibold text-muted-foreground">Groove</div>
                <div className="font-semibold text-primary">CSW</div>
              </div>
              <div className="space-y-4">
                {[
                  { feature: "Virtual Coworking", groove: "✓", csw: "✓" },
                  { feature: "Structured Sessions", groove: "50 min only", csw: "Customizable" },
                  { feature: "24/7 Availability", groove: "✗", csw: "✓" },
                  { feature: "Task Management", groove: "✗", csw: "✓" },
                  { feature: "Pomodoro Timer", groove: "✗", csw: "✓" },
                  { feature: "Lo-fi Music", groove: "✗", csw: "✓" },
                  { feature: "Themed Backgrounds", groove: "✗", csw: "✓" },
                  { feature: "Price", groove: "$18/month", csw: "Free Forever" },
                ].map((row, index) => (
                  <div key={index} className="grid grid-cols-3 gap-4 text-center py-3 border-t border-border/50">
                    <div className="text-muted-foreground text-sm">{row.feature}</div>
                    <div className="text-muted-foreground text-sm">{row.groove}</div>
                    <div className="text-primary text-sm font-medium">{row.csw}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-secondary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 mb-6">
                <HelpCircle className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">Frequently Asked Questions</span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Got <span className="text-gradient">Questions</span>?
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border border-border/50 rounded-xl px-6 bg-card/30 backdrop-blur-sm"
                >
                  <AccordionTrigger className="text-left text-foreground hover:text-primary transition-colors py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[150px]" />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground">
              Ready to <span className="text-gradient">Groove</span> Again?
            </h2>
            <p className="text-xl text-muted-foreground">
              Join 10,000+ users who've found their new coworking home. Free forever, no limits, no excuses.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start Coworking Free <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              No credit card required • No time limits • No subscription
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Groove;
